import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMenus,
  createMenu,
  updateMenu,
  resetMenuState
} from '../store/menuSlice';
import { fetchPods } from '../store/podSlice';
import { fetchMealTypes } from '../store/mealTypeSlice';
import { fetchSubItems } from '../store/subItemSlice';
import PodSelector from '../components/PodSelector';
import MealTypeSelector from '../components/MealTypeSelector';
import MenuList from '../components/MenuList';
import SubItemSelector from '../components/SubItemSelector';

const MenuScreen = () => {
  const dispatch = useDispatch();
  const { menus, success, isLoading, error } = useSelector((state) => state.menu);
  const { pods } = useSelector((state) => state.pods);
  const { mealTypes: mealtypes } = useSelector((state) => state.mealType);
  const { subItems } = useSelector((state) => state.subItem);

  const [form, setForm] = useState({
    name: '',
    podId: '',
    podText: '',
    mealtypeId: '',
    mealtypeText: '',
    cuisineId: null,
    description: '',
    price: 0,
    menutype: '',
    isveg: true,
    menustatus: 'Open',
    canupgradeasmeal: true,
    omakid: '',
    calories: 0,
    protein: 0,
    carbohydrates: 0,
    fat: 0,
    fiber: 0,
    serving: '',
    ishfmitem: false,
    tags: '',
    subItems: [],
    imageUrl: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    dispatch(fetchMenus());
    dispatch(fetchPods());
    dispatch(fetchMealTypes());
    dispatch(fetchSubItems());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      alert(isEditing ? 'Menu updated!' : 'Menu created!');
      dispatch(fetchMenus());

      setForm({
        name: '',
        podId: '',
        podText: '',
        mealtypeId: '',
        mealtypeText: '',
        cuisineId: '',
        description: '',
        price: 0,
        menutype: '',
        isveg: true,
        menustatus: 'Open',
        canupgradeasmeal: true,
        omakid: '',
        calories: 0,
        protein: 0,
        carbohydrates: 0,
        fat: 0,
        fiber: 0,
        serving: '',
        ishfmitem: false,
        tags: '',
        subItems: [],
        imageUrl: ''
      });
      setIsEditing(false);
      setSelectedId(null);
      dispatch(resetMenuState());
    }
  }, [success, isEditing, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handlePodSelect = (pod) => {
    setForm({ ...form, podId: pod.id, podText: pod.podname });
  };

  const handleMealTypeSelect = (type) => {
    setForm({ ...form, mealtypeId: type.id, mealtypeText: type.type });
  };

  const handleSubItemToggle = (subItemId) => {
    setForm((prevForm) => {
      const exists = prevForm.subItems.includes(subItemId);
      return {
        ...prevForm,
        subItems: exists
          ? prevForm.subItems.filter(id => id !== subItemId)
          : [...prevForm.subItems, subItemId]
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: Number(form.price),
      calories: Number(form.calories),
      protein: Number(form.protein),
      carbohydrates: Number(form.carbohydrates),
      fat: Number(form.fat),
      fiber: Number(form.fiber),
      cuisineId: form.cuisineId || null
    };
    delete payload.podText;
    delete payload.mealtypeText;

    if (isEditing && selectedId) {
      dispatch(updateMenu({ id: selectedId, formData: payload }));
    } else {
      dispatch(createMenu(payload));
    }
  };

  const handleEdit = (menu) => {
    setIsEditing(true);
    setSelectedId(menu.id);

    const podId = menu.podId || menu.pod?.id;
    const mealtypeId = menu.mealtypeId || menu.mealtype?.id;

    const pod = Array.isArray(pods) ? pods.find(p => p.id === podId) : null;
    const meal = Array.isArray(mealtypes) ? mealtypes.find(m => m.id === mealtypeId) : null;

    setForm({
      ...menu,
      podId: podId || '',
      mealtypeId: mealtypeId || '',
      podText: pod?.podname || '',
      mealtypeText: meal?.type || '',
      subItems: menu.subItems?.map(sub => sub.id) || [],
      imageUrl: menu.menuImages?.[0]?.imageUrl || ''
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const formData = new FormData();
    formData.append('image', file);
    formData.append('menuId', selectedId || ''); // if editing, send menuId
  
    try {
      const response = await fetch('https://api.monkeybox.ai/api/menuimages/upload', {
        method: 'POST',
        body: formData,
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setForm((prev) => ({
          ...prev,
          imageUrl: data.imageUrl
        }));
      } else {
        console.error('Upload failed', data.message || data);
        alert('Image upload failed');
      }
    } catch (error) {
      console.error('Error uploading:', error);
      alert('Upload error');
    }
  };

  return (
    <div className="space-y-10 px-6">
      <div className="max-w-5xl bg-white rounded shadow p-6 mx-auto space-y-4">
        <h2 className="text-2xl font-bold mb-4">
          {isEditing ? 'Edit Menu' : 'New Menu'}
        </h2>
        {error && <p className="text-red-500">{error}</p>}

        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <label>
            <span className="block text-sm font-medium text-gray-700 mb-1">Menu Name</span>
            <input name="name" value={form.name} onChange={handleChange} className="w-full px-4 py-2 border rounded" required />
          </label>

          <label>
            <span className="block text-sm font-medium text-gray-700 mb-1">Description</span>
            <input name="description" value={form.description} onChange={handleChange} className="w-full px-4 py-2 border rounded" />
          </label>

          <label>
            <span className="block text-sm font-medium text-gray-700 mb-1">Price</span>
            <input name="price" type="number" value={form.price} onChange={handleChange} className="w-full px-4 py-2 border rounded" />
          </label>

          <label>
            <span className="block text-sm font-medium text-gray-700 mb-1">Menu Type</span>
            <select name="menutype" value={form.menutype} onChange={handleChange} className="w-full px-4 py-2 border rounded bg-white">
              <option value="">Select Menu Type</option>
              <option value="Classic">Classic</option>
              <option value="Rolling">Rolling</option>
            </select>
          </label>

          <label>
            <span className="block text-sm font-medium text-gray-700 mb-1">Omak ID</span>
            <input name="omakid" value={form.omakid} onChange={handleChange} className="w-full px-4 py-2 border rounded" />
          </label>

          <div className="col-span-2">
            <PodSelector selectedPod={{ podname: form.podText }} onSelect={handlePodSelect} />
          </div>

          <div className="col-span-2">
            <MealTypeSelector
              selectedType={Array.isArray(mealtypes) ? mealtypes.find(m => m.id === form.mealtypeId) : null}
              onSelect={handleMealTypeSelect}
            />
          </div>

          <div className="col-span-2 flex items-start gap-4">
  <div className="flex-1">
    <label>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        Upload Image
      </span>
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        disabled={!selectedId} // ⬅️ Disable until menu exists
        className="w-full px-4 py-2 border rounded bg-white"
      />
      {!selectedId && (
        <p className="text-xs text-gray-500 mt-1">
          Save the menu first to upload an image
        </p>
      )}
    </label>
  </div>

  {form.imageUrl?.trim() && (
    <div className="w-40 h-40 flex-shrink-0 border rounded overflow-hidden">
      <img
        src={form.imageUrl}
        alt="Preview"
        className="w-full h-full object-cover"
      />
    </div>
  )}
</div>
          <div className="col-span-2 mt-6">
            <h3 className="text-lg font-semibold mb-2">Nutritional Factors</h3>
            <div className="grid grid-cols-2 gap-4">
              {['calories', 'protein', 'carbohydrates', 'fat', 'fiber', 'serving', 'tags'].map(field => (
                <label key={field}>
                  <span className="block text-sm font-medium text-gray-700 mb-1">{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                  <input
                    name={field}
                    value={form[field]}
                    type={field === 'calories' ? 'number' : 'text'}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border rounded"
                  />
                </label>
              ))}
            </div>
          </div>

          <SubItemSelector
            selectedSubItems={form.subItems}
            onToggle={handleSubItemToggle}
          />

          <div className="col-span-2">
            <button type="submit" disabled={isLoading} className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700">
              {isLoading ? 'Saving...' : isEditing ? 'Update Menu' : 'Create Menu'}
            </button>
          </div>
        </form>
      </div>

      <MenuList menus={menus} onEdit={handleEdit} />
    </div>
  );
};

export default MenuScreen;